import { DownloadSimpleIcon } from '@phosphor-icons/react'
import { endOfDay } from 'date-fns'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  GiftCardTopupRecord,
  getGiftCardTopups,
  useGetGiftCardTopups,
} from '@/api/rewards/get-gift-card-topups'
import { useUpdateGiftCardTopupStatus } from '@/api/rewards/update-gift-card-topup-status'
import { Page } from '@/components/layout/page'
import { TableGroup } from '@/components/layout/table-group'
import { Button } from '@/components/ui/button'
import { DatePickerInput } from '@/components/ui/date-picker-input'
import { Icon } from '@/components/ui/icon'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { Select } from '@/components/ui/select'
import { toastFactory } from '@/factories'
import { useFilters, useSearchParams } from '@/hooks'
import { DateRange } from '@/models/helpers'
import { selectBuildingsForFilter } from '@/reducers/buildingSelectorReducer'
import { selectUserRole } from '@/reducers/userDataReducer'
import { getApiErrorMessage } from '@/utils/error'
import { buildCsvFilename, downloadCsv } from '@/utils/file'

import {
  financialsTableInitialFilters,
  getCsvFields,
  getFinancialsTableColumns,
  TopupStatusEnum,
} from './financials.constants'
import { FinancialsTableFilters, financialsTableFiltersSchema } from './financials.types'
import { FinancialsHeader } from './header'

export const FinancialsPage = () => {
  const { t } = useTranslation(['communication'])
  const buildings = useSelector(selectBuildingsForFilter)
  const userRole = useSelector(selectUserRole)

  const searchParams = useSearchParams({
    initialState: financialsTableInitialFilters,
    schema: financialsTableFiltersSchema,
  })

  const tableFilters = useFilters<FinancialsTableFilters>({
    controlledState: searchParams.parsedQuery,
    initialState: financialsTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const filters = tableFilters.filters
  const queryParams = {
    buildingId: filters.buildingId,
    from: filters.fromDate ?? undefined,
    limit: filters.limit ?? DEFAULT_LIMIT,
    search: filters.search || undefined,
    skip: filters.skip ?? 0,
    to: filters.toDate ?? undefined,
  }

  const { data, isFetching } = useGetGiftCardTopups(queryParams)
  const { mutateAsync: approveTopup } = useUpdateGiftCardTopupStatus()
  const { mutateAsync: declineTopup } = useUpdateGiftCardTopupStatus()

  const [approvingIds, setApprovingIds] = useState(new Set<number>())
  const [decliningIds, setDecliningIds] = useState(new Set<number>())

  const fromDateFilter = tableFilters.getFilter('fromDate')
  const toDateFilter = tableFilters.getFilter('toDate')
  const dateRange: DateRange = [
    fromDateFilter ? new Date(fromDateFilter) : null,
    toDateFilter ? new Date(toDateFilter) : null,
  ]

  const buildingOptions = useMemo(
    () => buildings.map((b) => ({ label: b.text, value: b.id })),
    [buildings],
  )

  const handleDateChange = (range: DateRange) => {
    tableFilters.setFilters({
      fromDate: range[0]?.getTime(),
      toDate: range[1] ? endOfDay(range[1]).getTime() : undefined,
    })
  }

  const handleApprove = async (row: GiftCardTopupRecord) => {
    setApprovingIds((prev) => new Set(prev).add(row.id))
    try {
      await approveTopup({
        amount: row.amount,
        buildingId: row.buildingId,
        status: TopupStatusEnum.enum.approved,
        topupId: row.id,
        type: row.type,
      })
      toastFactory.success(t('communication:financials.approveSuccess'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    } finally {
      setApprovingIds((prev) => {
        const next = new Set(prev)
        next.delete(row.id)
        return next
      })
    }
  }

  const handleDecline = async (row: GiftCardTopupRecord) => {
    setDecliningIds((prev) => new Set(prev).add(row.id))
    try {
      await declineTopup({
        amount: row.amount,
        buildingId: row.buildingId,
        status: TopupStatusEnum.enum.declined,
        topupId: row.id,
        type: row.type,
      })
      toastFactory.success(t('communication:financials.declineSuccess'))
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    } finally {
      setDecliningIds((prev) => {
        const next = new Set(prev)
        next.delete(row.id)
        return next
      })
    }
  }

  const [isExporting, setIsExporting] = useState(false)

  const handleExportCsv = async () => {
    const total = data?.total ?? 0
    const buildingName = filters.buildingId
      ? buildings.find((b) => b.id === filters.buildingId)?.name
      : null
    const csvOptions = {
      fields: getCsvFields(t),
      filename: buildCsvFilename({
        building: buildingName,
        dateRange,
        page: 'financials',
      }),
    }

    if (!total) {
      downloadCsv({ ...csvOptions, data: [] })
      return
    }

    setIsExporting(true)
    try {
      const allData = await getGiftCardTopups({ ...queryParams, limit: total, skip: 0 })
      await downloadCsv({ ...csvOptions, data: allData.data })
    } finally {
      setIsExporting(false)
    }
  }

  const columns = useMemo(
    () =>
      getFinancialsTableColumns(t, {
        approvingIds,
        decliningIds,
        onApprove: handleApprove,
        onDecline: handleDecline,
        userRole,
      }),
    [t, userRole, approvingIds, decliningIds, handleApprove, handleDecline],
  )

  return (
    <Page>
      <FinancialsHeader />
      <TableGroup
        headerFiltersHorizontal
        columns={columns}
        data={data?.data ?? []}
        enableColumnVisibility={false}
        emptyNoFilters={{
          description: t('communication:financials.empty.noFilters.description'),
          title: t('communication:financials.empty.noFilters.title'),
        }}
        emptyWithFilters={{
          description: t('communication:financials.empty.withFilters.description'),
          title: t('communication:financials.empty.withFilters.title'),
        }}
        filters={
          <Select
            searchable
            name="building"
            options={buildingOptions}
            placeholder={t('communication:financials.building.placeholder')}
            size="small"
            value={tableFilters.getFilter('buildingId') ?? null}
            onChange={(value: number) => tableFilters.setFilter('buildingId', value)}
            onReset={() => tableFilters.setFilter('buildingId', undefined)}
          />
        }
        getRowKey={(row) => row.id}
        id="financials-table"
        loading={isFetching}
        navigation={{
          actions: (
            <Button
              onlyBox
              loading={isExporting}
              size="small"
              use="secondary"
              onClick={handleExportCsv}
            >
              <Icon icon={DownloadSimpleIcon} />
            </Button>
          ),
          datePicker: (
            <DatePickerInput
              mode="range"
              placeholder={t('communication:financials.dateRange.placeholder')}
              size="small"
              value={dateRange}
              onRangeChange={handleDateChange}
            />
          ),
        }}
        pagination={{
          paginationFilters: tableFilters,
          total: data?.total ?? 0,
        }}
        tableFilters={tableFilters}
      />
    </Page>
  )
}
